import * as React from 'react';
import { Button, CircularProgress, Grid } from '@mui/material';

import { AddressInput } from 'components';
import { IAddress } from 'models';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

import { useShippingAddressFormManager } from './shipping-address-form.hook';

interface ShippingAddressFormProps {
  onLoading?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const ShippingAddressForm = React.forwardRef(({ onLoading = () => {}, next }: ShippingAddressFormProps, ref) => {
  const [formManager, { isLoading }] = useShippingAddressFormManager({ next });

  React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);

  React.useEffect(() => onLoading(isLoading), [isLoading]);

  const { shippingAddress } = useAppSelector(selectCurrentUserInfo);

  React.useEffect(() => {
    formManager.setValues({
      shipping_address1: shippingAddress?.address_1 || '',
      shipping_address2: shippingAddress?.address_2 || '',
      shipping_city: shippingAddress?.city || '',
      shipping_state: shippingAddress?.state || '',
      shipping_zip: shippingAddress?.zip || '',
    });
  }, [shippingAddress]);

  React.useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        formManager.handleSubmit();
      },
    }),
    [],
  );

  return (
    <form onSubmit={formManager.handleSubmit} autoComplete="off">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <AddressInput
            data={{
              id: -1,
              address_1: formManager.values.shipping_address1,
              address_2: formManager.values.shipping_address2,
              city: formManager.values.shipping_city,
              state: formManager.values.shipping_state,
              zip: formManager.values.shipping_zip,
            }}
            errors={{
              address_1: formManager.touched.shipping_address1 ? formManager.errors.shipping_address1 || '' : '',
              address_2: formManager.touched.shipping_address2 ? formManager.errors.shipping_address2 || '' : '',
              city: formManager.touched.shipping_city ? formManager.errors.shipping_city || '' : '',
              state: formManager.touched.shipping_state ? formManager.errors.shipping_state || '' : '',
              zip: formManager.touched.shipping_zip ? formManager.errors.shipping_zip || '' : '',
            }}
            onChangeData={(data: IAddress) => {
              const { address_1, address_2, city, state, zip } = data;

              formManager.setValues({
                ...formManager.values,
                shipping_address1: address_1 as string,
                shipping_address2: address_2 as string,
                shipping_city: city as string,
                shipping_state: state as string,
                shipping_zip: zip as string,
              });
            }}
          />
        </Grid>

        <Grid item xs={12} hidden>
          <Button
            variant="contained"
            type="submit"
            onClick={() => formManager.handleSubmit()}
            endIcon={
              isLoading ? (
                <CircularProgress
                  color="inherit"
                  size={14}
                  sx={{
                    marginLeft: 0.5,
                  }}
                />
              ) : null
            }
            sx={{
              paddingX: 2.5,
            }}
          >
            Update Shipping Address
          </Button>
        </Grid>
      </Grid>
    </form>
  );
});
